// Model Selection Constants
//
// Every id here is UNQUALIFIED (no `provider/` prefix), and that is load
// bearing rather than cosmetic: the server routes an unqualified id to the
// Claude Agent SDK. These three strings are the whole Claude set and must keep
// their exact wire form. See MULTI_MODEL_OPTIONS below for the models that
// route to the opencode harness instead.
export const MODELS = {
  HAIKU: 'claude-haiku-4-5-20251001',  // Haiku 4.5
  SONNET: 'claude-sonnet-5',           // Sonnet 5
  OPUS: 'claude-opus-5'                // Opus 5 (dateless ID per the 4.6+ naming convention)
};

export const DEFAULT_MODEL = MODELS.OPUS;

// ONE NAMING SYSTEM, AND WHY IT CHANGED.
//
// These rows used to read "Claude Fast / Smart / Pro / Pro+", and that was the
// right call while Claude was the only maker in the picker: with a single
// vendor, capability tiers spared the user a model-identity decision they had
// no basis to make, and a test enforced that the internal names never reached
// the UI.
//
// What changed is the list, not the opinion. It now carries models from four
// makers, and a single list cannot hold two naming systems. A row reading
// "Smart" directly above one reading "GPT-5.6 Sol" forces the user to work out
// which scheme they are reading before they can compare anything, and the tier
// words stop meaning anything relative once "Pro" sits beside another vendor's
// "Pro". Real names on every row is the consistent choice precisely BECAUSE the
// list is no longer Claude-only. Reintroducing tier labels AS ROW LABELS would
// reintroduce that mismatch.
//
// TIER WORDS AS SECTION HEADERS ARE THE OPPOSITE MOVE, NOT A REVERSAL OF IT.
// The picker now groups its rows under Pro / Smart / Fast (CAPABILITY_SECTIONS
// below), and the distinction is worth stating plainly because the two things
// use the same three words:
//
//   - A tier word REPLACING a model name destroys information. The row stops
//     saying which model runs, and it collides with the neighbouring row that
//     kept its real name. That is what we removed and are not bringing back.
//   - A tier word ABOVE rows that keep their real names ADDS information. It
//     answers "how capable" once, for a group, and every row underneath still
//     says exactly which model it is. Nothing is hidden and nothing collides.
//
// The header is also what let the per-row capability meter go. Drawing dots on
// every row under a header that already says "Pro" is the same fact twice, so
// each row is now a name plus a single signal: cost.
//
// LABEL LENGTH IS PART OF THE NAME NOW, BECAUSE THE CHIP SHARES ONE ROW WITH
// THE INPUT. The composer collapsed to a single row, so the model chip no
// longer sits on a line of its own: in a 320px pane every character it spends
// is a character the user cannot type into. The rule that shortened these is
// the narrowest one that keeps every row a real model name: DROP THE MAKER'S
// BRAND WHERE THE FAMILY NAME ALREADY IDENTIFIES THE MODEL, and change nothing
// else.
//
// That is why "Claude Haiku 4.5" is now "Haiku 4.5" while "Gemini 3.6 Flash"
// keeps its first word, which looks inconsistent and is not. Opus, Sonnet and
// Haiku are unique tokens that name a model on their own, and Anthropic writes
// them that way. Gemini's tiers are Pro, Flash and Flash Lite: ordinary words
// that name nothing without "Gemini" in front of them. Applying one rule to
// both surfaces would produce "3.6 Flash", which is not a model name at all.
//
// The maker did not go missing, it moved to the chip tooltip, which is exactly
// where `providerLabel` already lives for the same reason.
//
// WHAT IS NOT ALLOWED IS BUYING CHARACTERS WITH INVENTED NAMES. A tier
// nickname in place of a model name is the thing this list removed (above),
// and trimming a real name down to something no maker ships is the same
// mistake wearing a different coat. "Gemini 3.5 Flash Lite" is the one label
// still longer than the rest manage, and it stays: "Gemini 3.5 Lite" is a name
// Google does not use, and "Gemini 3.5 Flash" is the name of a DIFFERENT model
// than the one the row would run.
//
// Two levels per model, and only two. `capabilityLevel` decides which SECTION
// a model lands in; `costLevel` is what it burns and is the one thing drawn on
// the row. Both are 1-4. They move together on the Claude ladder and
// deliberately do not for every other model, which is the whole point of the
// grouping: inside a capability band, price is the remaining decision, and
// Qwen3.8 Max at $2/$6 sitting beside Claude Sonnet 5 at $3/$15 is exactly the
// cross-maker comparison a flat list buried.
//
// `tooltip` is used by the composer CHIP only, and it is where the maker's
// brand went when the labels lost it: it costs a hover rather than a third of
// the row. The rows carry no hover tooltip:
// the name is now the model's real name, so a popup restating it is noise, and
// in a 375px pane a popup over a list is actively in the way. The row's
// accessible name still names its section and its cost, so nothing the eye gets
// from the grouping is lost to a screen reader.
export const MODEL_COPY = {
  haiku: {
    label: 'Haiku 4.5',
    tooltip: 'Claude Haiku 4.5 - fastest, for quick edits and simple slides',
    capabilityLevel: 1,
    costLevel: 1
  },
  sonnet: {
    label: 'Sonnet 5',
    tooltip: 'Claude Sonnet 5 - balanced, for everyday slide work',
    capabilityLevel: 3,
    costLevel: 3
  },
  opus: {
    label: 'Opus 5',
    tooltip: 'Claude Opus 5 - deepest thinking for complex decks (default)',
    capabilityLevel: 4,
    costLevel: 4
  }
};

// ===========================================================================
// The three capability sections
// ===========================================================================
//
// ONE RULE DECIDES PLACEMENT: a model's section is derived from its
// `capabilityLevel` and nothing else. There is no per-model section field and
// no override list, deliberately. A hand-placed exception sitting next to a
// stated rule reads as a bug to the next person, who moves it back; keeping
// the level as the single source of truth means a placement decision is made
// by editing the number, in the open, next to the reasoning for it.
//
// Order is most capable first, so the default (Opus 5) is in the first
// section rather than scrolled to.
export const CAPABILITY_SECTIONS = Object.freeze([
  Object.freeze({ id: 'pro', label: 'Pro', levels: Object.freeze([4]) }),
  Object.freeze({ id: 'smart', label: 'Smart', levels: Object.freeze([3]) }),
  // Two levels share one section. Three bands is what the list can carry at
  // this length; a fourth would put one or two rows under their own heading,
  // which is more furniture than choice.
  Object.freeze({ id: 'fast', label: 'Fast', levels: Object.freeze([1, 2]) }),
]);

/**
 * Group a flat option list into the shipped sections.
 *
 * Within a section the CHEAPEST goes first, so price reads down the column and
 * the cross-maker comparison is the one the eye makes on its own. Ties are
 * broken by source order, which puts Claude at the head of its band: inside a
 * single cost band the difference is below what a `$` mark resolves, so any
 * finer ordering would be precision the signal does not actually show.
 *
 * An empty section is dropped rather than drawn as a heading over nothing,
 * which is what happens to Pro and Smart when the opencode-routed models are
 * withheld and only the three Claude entries remain.
 */
export function groupModelsByCapability(options) {
  return CAPABILITY_SECTIONS
    .map((section) => ({
      ...section,
      options: options
        .filter((option) => section.levels.includes(option.capabilityLevel))
        // Array.prototype.sort is stable, so equal cost keeps source order.
        .sort((a, b) => a.costLevel - b.costLevel),
    }))
    .filter((section) => section.options.length > 0);
}

// ===========================================================================
// The models that route to the opencode harness
// ===========================================================================
//
// A PROVIDER-QUALIFIED id is the only thing that moves a turn off the Claude
// Agent SDK and onto the opencode harness, so these strings are sent verbatim
// and must never be prettified or shortened for display.
//
// The server splits provider from model on the FIRST slash. That is why an
// OpenRouter id keeps its vendor path intact: `openrouter/moonshotai/kimi-k3`
// is provider `openrouter` plus model `moonshotai/kimi-k3`. Dropping the
// vendor half would ask OpenRouter for a model it does not have, and trimming
// to the last segment would ask it for one it has never heard of.
//
// `anthropic/...` is deliberately absent. It looks like a second road to
// Claude and is not one: the server sends it back to the Claude SDK anyway,
// so offering it would be a picker entry that silently means something else.
export const MULTI_MODEL_PROVIDERS = Object.freeze({
  OPENAI: 'openai',
  OPENROUTER: 'openrouter',
});

const MULTI_MODEL_PROVIDER_LABELS = Object.freeze({
  [MULTI_MODEL_PROVIDERS.OPENAI]: 'OpenAI',
  [MULTI_MODEL_PROVIDERS.OPENROUTER]: 'OpenRouter',
});

// `providerLabel` is kept but is NOT drawn as its own column in the picker.
// It was the marker that made these entries look like a lesser class of thing,
// and the model names already say who made them. It survives in the chip
// tooltip and the accessible name, where it costs the list nothing.
function multiModelOption({ key, model, label, capabilityLevel, costLevel }) {
  const provider = model.slice(0, model.indexOf('/'));
  const providerLabel = MULTI_MODEL_PROVIDER_LABELS[provider];
  if (!providerLabel) {
    throw new Error(`Unrouted model provider: ${provider}`);
  }
  return Object.freeze({
    id: key,
    model,
    label,
    capabilityLevel,
    costLevel,
    provider,
    providerLabel,
    tooltip: `${label} via ${providerLabel}`,
  });
}

// VERSION CURRENCY, last established 2026-08-05.
//
// Every id below is the newest shipping member of its family on the day it was
// checked, taken from the provider that actually serves it: the OpenRouter
// catalogue (`openrouter.ai/api/v1/models`) for the six OpenRouter entries,
// and OpenAI's own pricing and model docs
// (`developers.openai.com/api/docs/pricing` and `.../models`) for the three
// that route to OpenAI directly. A family moves faster than this file does, so
// re-check against those two sources rather than against memory.
//
// What moved at the last check:
//   - The whole GPT-5.6 family is now offered, not just its flagship. OpenAI
//     publishes exactly three: `gpt-5.6-sol` (frontier), `gpt-5.6-terra`
//     (balanced) and `gpt-5.6-luna` (high-volume, budget). There is no 5.6
//     Pro.
//   - GPT-5.5 Pro was REMOVED. It is still published, but it was the only
//     model on the menu with no prompt caching at all and the steepest rate by
//     a factor of six, and the 5.6 family covers the same frontier band far
//     more cheaply. Its price-table entry survives for in-flight turns; see
//     RETIRED_MODEL_IDS below.
//   - GLM-5.2, Qwen3.8 Max and Kimi K3 were all already current.
//   - Grok 4.6 added to Pro. Its price is not in modelPriceTable.js, which is
//     the same deliberate choice made for the Gemini rows: OpenRouter routes
//     span tiers, so a flat entry would silently misprice a routed request and
//     the harness-reported cost is used instead.
//   - Gemini 3.1 Pro Preview, Gemini 3.6 Flash, and Gemini 3.5 Flash Lite
//     were added with their exact OpenRouter ids. Their routes span multiple
//     price tiers, so the picker uses the floor for its coarse cost band while
//     backend fallback pricing deliberately remains unset.
//
// COST BANDS. Ranked on a blended rate (input plus a fifth of output, which is
// roughly the shape of an Office turn: a large prompt, a moderate answer),
// using each provider's published USD per million tokens on this date:
//   1 ($)    gpt-5.6-luna, Gemini 3.5 Flash Lite, GLM-5.2, Claude Haiku 4.5
//   2 ($$)   Qwen3.8 Max, GPT-5.6 Terra, Gemini 3.1 Pro and Gemini 3.6 Flash
//   3 ($$$)  Kimi K3 and Claude Sonnet 5
//   4 ($$$$) Claude Opus 5 and GPT-5.6 Sol
// Kimi and Sonnet sit on identical published rates, which is why they carry
// identical cost marks even though the two now sit in different sections.
// Getting this order wrong in either direction tells the user the dearer model
// is the cheaper one, which is the one thing a cost signal must never do, and
// it is now the ONLY signal drawn on a row.
export const MULTI_MODEL_OPTIONS = Object.freeze([
  multiModelOption({
    key: 'gpt-5-6-sol',
    model: 'openai/gpt-5.6-sol',
    label: 'GPT-5.6 Sol',
    capabilityLevel: 4,
    costLevel: 4,
  }),
  multiModelOption({
    key: 'gpt-5-6-terra',
    model: 'openai/gpt-5.6-terra',
    label: 'GPT-5.6 Terra',
    capabilityLevel: 3,
    costLevel: 2,
  }),
  multiModelOption({
    key: 'gpt-5-6-luna',
    model: 'openai/gpt-5.6-luna',
    label: 'GPT-5.6 Luna',
    capabilityLevel: 1,
    costLevel: 1,
  }),
  // "Preview" is dropped from the LABEL and kept in the ID, which is the only
  // place it decides anything. It is a release channel rather than part of the
  // model's name: the model is Gemini 3.1 Pro, and no other row on this menu
  // states a channel either. It was also 8 of the 22 characters on what was
  // the longest label in the picker, which is the whole reason to look at it.
  // If Google ships a GA `gemini-3.1-pro` and it is added here, the two rows
  // would read identically; the duplicate-label test fails the moment that
  // entry is written rather than after it ships.
  multiModelOption({
    key: 'gemini-3-1-pro-preview',
    model: 'openrouter/google/gemini-3.1-pro-preview',
    label: 'Gemini 3.1 Pro',
    capabilityLevel: 4,
    costLevel: 2,
  }),
  // Verified against OpenRouter's LIVE model API (410 models) and then end to
  // end through a real opencode server: dispatch accepted, the assistant row
  // came back stamped `x-ai/grok-4.6` with real tokens.
  //
  // Worth recording because the obvious check is the wrong one. opencode's
  // bundled catalog lists 337 models and does NOT contain grok-4.6, so reading
  // the catalog says the model does not exist. It does; the catalog simply
  // lags, and opencode passes an unlisted id through to the provider intact.
  // Trust openrouter.ai/api/v1/models, which is public and needs no key.
  //
  // The grok-latest alias is deliberately not used: a label whose model changes
  // underneath it makes both the cost mark and the user's choice a lie.
  //
  // 2 in / 6 out per million, so blended 2 + 6/5 = 3.2, band 2: below Sonnet 5
  // at 6.0 and Opus 5 at 10.0, above Haiku 4.5 at 2.0.
  multiModelOption({
    key: 'grok-4-6',
    model: 'openrouter/x-ai/grok-4.6',
    label: 'Grok 4.6',
    capabilityLevel: 4,
    costLevel: 2,
  }),
  multiModelOption({
    key: 'gemini-3-6-flash',
    model: 'openrouter/google/gemini-3.6-flash',
    label: 'Gemini 3.6 Flash',
    capabilityLevel: 3,
    costLevel: 2,
  }),
  multiModelOption({
    key: 'gemini-3-5-flash-lite',
    model: 'openrouter/google/gemini-3.5-flash-lite',
    label: 'Gemini 3.5 Flash Lite',
    capabilityLevel: 1,
    costLevel: 1,
  }),
  // KIMI IS RATED 4 BY OWNER DIRECTION, AND THAT IS WHAT PUTS IT IN PRO.
  //
  // Read on our own reading of the model this would be a 3, and it shipped as
  // a 3. The owner placed it in Pro, and rather than special-case the
  // placement, the level itself moves: section membership is derived from
  // `capabilityLevel` alone (see CAPABILITY_SECTIONS), so a level of 3 next to
  // a Pro placement would be a contradiction the next reader "fixes" by
  // dragging it back to Smart. One number, one source of truth, decision
  // recorded here.
  //
  // The supporting facts, which are not themselves the reason: a 1M context
  // window, and a published rate of $3 in / $15 out, the same band as Claude
  // Sonnet. It is also the cheapest row in Pro, which makes it the clearest
  // instance of the comparison the sections exist to expose.
  multiModelOption({
    key: 'kimi-k3',
    model: 'openrouter/moonshotai/kimi-k3',
    label: 'Kimi K3',
    capabilityLevel: 4,
    costLevel: 3,
  }),
  multiModelOption({
    key: 'glm-5-2',
    model: 'openrouter/z-ai/glm-5.2',
    label: 'GLM-5.2',
    capabilityLevel: 2,
    costLevel: 1,
  }),
  multiModelOption({
    key: 'qwen3-8-max',
    model: 'openrouter/qwen/qwen3.8-max',
    label: 'Qwen3.8 Max',
    capabilityLevel: 3,
    costLevel: 2,
  }),
]);

export const CLAUDE_MODEL_IDS = Object.freeze(Object.values(MODELS));
export const MULTI_MODEL_IDS = Object.freeze(
  MULTI_MODEL_OPTIONS.map((option) => option.model)
);

// Ids this build used to offer and no longer does. A returning user has one of
// these sitting in localStorage under MODEL_STORAGE_KEY, and it would otherwise
// keep being posted for every turn: `claude-fable-5` is a live Claude id, so it
// would run and bill silently under a chip reading "Opus 5", and
// `openai/gpt-5.5-pro` would run on the opencode harness with nothing on screen
// saying which model answered. Neither is a broken selection the user can see,
// which is exactly why it has to be corrected in code. AIInputSurface resets
// these to DEFAULT_MODEL on render; see the note on MODEL_STORAGE_KEY for why
// this is not handled by bumping the key.
export const RETIRED_MODEL_IDS = Object.freeze([
  'claude-fable-5',
  'openai/gpt-5.5-pro',
]);

export function isRetiredModelId(model) {
  return RETIRED_MODEL_IDS.includes(model);
}

/**
 * The picker entry for a stored model string, or null when it is not one of
 * ours. Membership is derived from the shipped list rather than re-deriving
 * the server's provider rule, so a model this build does not offer can never
 * be rendered as though it were on the menu.
 */
export function findMultiModelOption(model) {
  return MULTI_MODEL_OPTIONS.find((option) => option.model === model) || null;
}

// Storage key bumped to _v5 alongside the Opus 4.8 → Opus 5 upgrade (same
// pattern as the _v4 bump for Sonnet 4.6 → Sonnet 5). Returning users had
// `claude-opus-4-8` persisted here; since that string no longer matches
// any entry in MODELS, it would (a) keep sending the legacy 4.8 model to
// the backend and (b) mislabel the chip (the key-derivation falls back to
// 'fast' when the stored string matches no model). Bumping the key drops
// the stale value so DEFAULT_MODEL (Opus 5) cleanly takes over.
//
// STAYS _v5 through the multi-model additions AND through the Fable / GPT-5.5
// Pro retirements. A bump is a blunt instrument: it resets the model choice of
// EVERY user, including the large majority whose stored id still means exactly
// what it always did. Only two persisted strings changed meaning, so only those
// two are corrected, by id, at render time. See RETIRED_MODEL_IDS above.
export const MODEL_STORAGE_KEY = 'selectedModel_v5';
